/**
 * CashflowTab — Flux de trésorerie réels (Banque + Wave + Caisse) + projection.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  RefreshCw, Download, ChevronDown, ChevronRight, Plus, Trash2, TrendingUp, TrendingDown, Wallet,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const xof = (v) => `${Math.round(v || 0).toLocaleString('fr-FR')} XOF`;
const compact = (v) => {
  const n = Math.abs(v || 0);
  if (n >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)} Md`;
  if (n >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} M`;
  if (n >= 1_000) return `${Math.round(v / 1_000)} k`;
  return `${Math.round(v || 0)}`;
};

const Kpi = ({ title, value, subtext, icon: Icon, tone, testid }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm" data-testid={testid}>
    <div className="flex items-start justify-between">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</span>
      <Icon className={`w-4 h-4 ${tone}`} />
    </div>
    <div className="text-xl font-bold mt-2 text-gray-900">{value}</div>
    <div className="text-xs text-gray-500 mt-1">{subtext}</div>
  </div>
);

const CashflowTab = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [granularity, setGranularity] = useState('month');
  const [periods, setPeriods] = useState('12');
  const [source, setSource] = useState('all');
  const [showForecast, setShowForecast] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [cashOpen, setCashOpen] = useState(false);
  const [cashEntries, setCashEntries] = useState([]);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), direction: 'out', amount: '', label: '', category: 'Caisse' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/finance/cockpit/cashflow?granularity=${granularity}&periods=${periods}&source=${source}&include_forecast=${showForecast}`,
        { withCredentials: true },
      );
      setData(res.data);
    } catch (e) {
      toast({ title: 'Erreur', description: 'Chargement des flux impossible', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [granularity, periods, source, showForecast]);

  const fetchCashEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/finance/cockpit/cash-entries`, { withCredentials: true });
      setCashEntries(res.data?.items || []);
    } catch (e) { /* silencieux */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchCashEntries(); }, [fetchCashEntries]);

  const saveCashEntry = async () => {
    try {
      await axios.post(`${API}/admin/finance/cockpit/cash-entries`, {
        ...form, amount: Number(form.amount),
      }, { withCredentials: true });
      toast({ title: 'Mouvement enregistré' });
      setForm({ ...form, amount: '', label: '' });
      setCashOpen(false);
      fetchCashEntries();
      fetchData();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Enregistrement impossible', variant: 'destructive' });
    }
  };

  const deleteCashEntry = async (id) => {
    try {
      await axios.delete(`${API}/admin/finance/cockpit/cash-entries/${id}`, { withCredentials: true });
      fetchCashEntries();
      fetchData();
    } catch (e) {
      toast({ title: 'Erreur', description: 'Suppression impossible', variant: 'destructive' });
    }
  };

  const exportCsv = () => {
    if (!data) return;
    const rows = [['Période', 'Encaissements', 'Décaissements', 'Flux net', 'Solde fin de période']];
    data.series.forEach(s => rows.push([s.label, s.inflow, s.outflow, s.net, s.balance]));
    const csv = rows.map(r => r.join(';')).join('\n');
    const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `flux-tresorerie-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const s = data?.summary || {};
  const chartData = [
    ...(data?.series || []).map(x => ({ ...x, balanceReal: x.balance })),
    ...(showForecast ? (data?.forecast || []).map(x => ({ ...x, balanceForecast: x.balance })) : []),
  ];
  // Raccord visuel entre réel et projection
  if (chartData.length && data?.forecast?.length && showForecast) {
    const lastReal = chartData[(data.series?.length || 1) - 1];
    if (lastReal) lastReal.balanceForecast = lastReal.balance;
  }

  return (
    <div className="space-y-4" data-testid="tab-cashflow">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Flux de trésorerie</h2>
          <p className="text-sm text-gray-600">Argent réellement encaissé et décaissé (banque, Wave, caisse), projection en pointillés.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={granularity} onValueChange={(v) => { setGranularity(v); setPeriods(v === 'week' ? '12' : '12'); }}>
            <SelectTrigger className="w-[160px] bg-white" data-testid="cashflow-granularity"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Mensuel</SelectItem>
              <SelectItem value="week">Hebdomadaire</SelectItem>
            </SelectContent>
          </Select>
          <Select value={periods} onValueChange={setPeriods}>
            <SelectTrigger className="w-[160px] bg-white" data-testid="cashflow-periods"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(granularity === 'week' ? ['8', '12', '24'] : ['6', '12', '24']).map(p => (
                <SelectItem key={p} value={p}>{p} {granularity === 'week' ? 'semaines' : 'mois'}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="w-[160px] bg-white" data-testid="cashflow-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes sources</SelectItem>
              <SelectItem value="bank">Banque</SelectItem>
              <SelectItem value="wave">Wave</SelectItem>
              <SelectItem value="cash">Caisse</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 px-2">
            <Switch checked={showForecast && source === 'all'} disabled={source !== 'all'}
                    onCheckedChange={setShowForecast} data-testid="cashflow-forecast-toggle" />
            <span className={`text-xs ${source === 'all' ? 'text-gray-600' : 'text-gray-400'}`}
                  title={source === 'all' ? '' : 'Projection disponible uniquement en vue consolidée'}>Projection</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => setCashOpen(true)} data-testid="cashflow-add-cash-btn">
            <Plus className="w-4 h-4 mr-1" />Caisse
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="cashflow-export-btn">
            <Download className="w-4 h-4 mr-1" />CSV
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="cashflow-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading && !data ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />) : (
          <>
            <Kpi testid="cashflow-kpi-inflow" title="Encaissements" value={xof(s.inflow)} subtext="sur la période" icon={TrendingUp} tone="text-emerald-500" />
            <Kpi testid="cashflow-kpi-outflow" title="Décaissements" value={xof(s.outflow)} subtext={`Burn moyen ${compact(s.burnRate)} / période`} icon={TrendingDown} tone="text-red-500" />
            <Kpi testid="cashflow-kpi-net" title="Flux net" value={xof(s.net)} subtext={`Moyenne ${compact(s.avgNet)} / période`} icon={s.net >= 0 ? TrendingUp : TrendingDown} tone={s.net >= 0 ? 'text-emerald-500' : 'text-red-500'} />
            <Kpi testid="cashflow-kpi-balance" title="Solde actuel" value={xof(data?.currentBalance)} subtext={`${(data?.balanceSources || []).map(x => x === 'bank' ? 'Banque' : x === 'wave' ? 'Wave' : 'Caisse').join(' + ') || 'Aucune source'}${s.runwayMonths ? ` · autonomie ≈ ${s.runwayMonths} périodes` : ''}`} icon={Wallet} tone="text-blue-500" />
          </>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        {loading && !data ? <Skeleton className="h-72 w-full" /> : (
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tickFormatter={compact} tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tickFormatter={compact} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, n) => [xof(v), n]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="left" dataKey="inflow" name="Encaissements" fill="#10b981" radius={[3, 3, 0, 0]} />
              <Bar yAxisId="left" dataKey="outflow" name="Décaissements" fill="#ef4444" radius={[3, 3, 0, 0]} />
              <Line yAxisId="left" type="monotone" dataKey="net" name="Flux net" stroke="#0f172a" strokeWidth={2} dot={{ r: 2 }} />
              <Line yAxisId="right" type="monotone" dataKey="balanceReal" name="Solde de trésorerie" stroke="#2563eb" strokeWidth={2} dot={false} connectNulls />
              {showForecast && (
                <Line yAxisId="right" type="monotone" dataKey="balanceForecast" name="Solde projeté" stroke="#2563eb" strokeWidth={2} strokeDasharray="6 4" dot={false} connectNulls />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200 text-sm font-medium text-gray-700">Détail par période</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cashflow-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['', 'Période', 'Encaissements', 'Décaissements', 'Flux net', 'Solde fin de période', 'Mouvements'].map((h, i) => (
                  <th key={i} className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.series || []).map((row) => (
                <React.Fragment key={row.period}>
                  <tr className="border-b border-gray-100 hover:bg-gray-50/70 cursor-pointer"
                      onClick={() => setExpanded(expanded === row.period ? null : row.period)}
                      data-testid={`cashflow-row-${row.period}`}>
                    <td className="px-3 py-2.5 text-gray-400">{expanded === row.period ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                    <td className="px-3 py-2.5 font-medium text-gray-900">{row.label}</td>
                    <td className="px-3 py-2.5 text-emerald-700">{xof(row.inflow)}</td>
                    <td className="px-3 py-2.5 text-red-600">{xof(row.outflow)}</td>
                    <td className={`px-3 py-2.5 font-semibold ${row.net >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{xof(row.net)}</td>
                    <td className="px-3 py-2.5 text-gray-700">{xof(row.balance)}</td>
                    <td className="px-3 py-2.5 text-gray-500">{row.txCount}</td>
                  </tr>
                  {expanded === row.period && (
                    <tr className="bg-gray-50/60 border-b border-gray-100">
                      <td colSpan={7} className="px-6 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-xs">
                          <div>
                            <div className="font-medium text-gray-700 mb-2">Sorties par catégorie</div>
                            {(row.outflowByCategory || []).length === 0 ? <span className="text-gray-400">Aucune</span> :
                              row.outflowByCategory.map(c => (
                                <div key={c.name} className="flex justify-between py-0.5"><span className="text-gray-600">{c.name}</span><span className="text-red-600">{compact(c.amount)}</span></div>
                              ))}
                          </div>
                          <div>
                            <div className="font-medium text-gray-700 mb-2">Entrées par catégorie</div>
                            {(row.inflowByCategory || []).length === 0 ? <span className="text-gray-400">Aucune</span> :
                              row.inflowByCategory.map(c => (
                                <div key={c.name} className="flex justify-between py-0.5"><span className="text-gray-600">{c.name}</span><span className="text-emerald-700">{compact(c.amount)}</span></div>
                              ))}
                          </div>
                          <div>
                            <div className="font-medium text-gray-700 mb-2">Répartition par source</div>
                            {['bank', 'wave', 'cash'].map(k => (
                              <div key={k} className="flex justify-between py-0.5">
                                <span className="text-gray-600">{k === 'bank' ? 'Banque' : k === 'wave' ? 'Wave' : 'Caisse'}</span>
                                <span className="text-gray-700">+{compact(row.bySource?.[k]?.in)} / −{compact(row.bySource?.[k]?.out)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {(data?.forecast || []).length > 0 && showForecast && (data.forecast).map(f => (
                <tr key={f.period} className="border-b border-gray-100 bg-blue-50/40" data-testid={`cashflow-forecast-row-${f.period}`}>
                  <td className="px-3 py-2.5" />
                  <td className="px-3 py-2.5 font-medium text-blue-800">{f.label} <span className="text-xs font-normal">(projeté)</span></td>
                  <td className="px-3 py-2.5 text-emerald-700">{xof(f.inflow)}</td>
                  <td className="px-3 py-2.5 text-red-600">{xof(f.outflow)}</td>
                  <td className={`px-3 py-2.5 font-semibold ${f.net >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{xof(f.net)}</td>
                  <td className="px-3 py-2.5 text-blue-800">{xof(f.balance)}</td>
                  <td className="px-3 py-2.5 text-gray-400">—</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {cashEntries.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-4 py-3 border-b border-gray-200 text-sm font-medium text-gray-700">Mouvements de caisse saisis</div>
          <div className="divide-y divide-gray-100" data-testid="cash-entries-list">
            {cashEntries.slice(0, 10).map(e => (
              <div key={e.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                <div className="flex items-center gap-3">
                  <span className="text-gray-500 text-xs w-24">{e.date}</span>
                  <span className={e.direction === 'in' ? 'text-emerald-700' : 'text-red-600'}>
                    {e.direction === 'in' ? '+' : '−'}{xof(e.amount)}
                  </span>
                  <span className="text-gray-600">{e.label || e.category}</span>
                </div>
                <Button variant="ghost" size="sm" className="h-7 text-red-500" onClick={() => deleteCashEntry(e.id)} data-testid={`delete-cash-entry-${e.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={cashOpen} onOpenChange={setCashOpen}>
        <DialogContent data-testid="cash-entry-dialog">
          <DialogHeader><DialogTitle>Mouvement de caisse</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="cash-entry-date" />
              </div>
              <div>
                <Label className="text-xs">Sens</Label>
                <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                  <SelectTrigger data-testid="cash-entry-direction"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Entrée</SelectItem>
                    <SelectItem value="out">Sortie</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Montant (XOF)</Label>
              <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" data-testid="cash-entry-amount" />
            </div>
            <div>
              <Label className="text-xs">Libellé</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ex. achat carburant" data-testid="cash-entry-label" />
            </div>
            <div>
              <Label className="text-xs">Catégorie</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="cash-entry-category" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCashOpen(false)}>Annuler</Button>
            <Button onClick={saveCashEntry} disabled={!form.amount} data-testid="cash-entry-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashflowTab;
